import React from 'react';
import { toast } from 'sonner';
import { ExternalLink, Copy, X } from 'lucide-react';
import { copyToClipboard } from '../lib/clipboard';

/**
 * Iter 126 — Fallback modal shown when `redirectExternal` cannot break
 * out of the iframe AND popups are blocked. Presents the URL as a
 * clickable anchor with `target="_top"` so the browser handles the
 * navigation natively, plus a copy button as a last-ditch escape hatch.
 *
 * Renders nothing when `url` is falsy — safe to leave mounted permanently.
 *
 * Used by:
 *  - SubscriptionWidget (Admin Dashboard "Manage in Stripe")
 *  - AdminBilling (Checkout + Portal buttons)
 */
export default function ManualOpenModal({ url, onClose, testIdPrefix = 'manual-open' }) {
  if (!url) return null;

  const handleCopy = async () => {
    const ok = await copyToClipboard(url, { silent: true });
    if (ok) toast.success('Link copied — paste into a new browser tab.');
    else toast.error('Copy failed. Select and copy the URL below.');
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      data-testid={`${testIdPrefix}-modal`}
    >
      <div
        className="surface border border-soft rounded-2xl p-6 max-w-md w-full shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-fg-muted">Open Stripe</div>
            <h3 className="font-heading font-bold text-lg mt-1">Continue to Stripe</h3>
          </div>
          <button type="button" onClick={onClose} className="text-fg-muted hover:text-fg" data-testid={`${testIdPrefix}-close`}>
            <X className="w-5 h-5"/>
          </button>
        </div>
        <p className="text-sm text-fg-muted mb-4">
          Your browser blocked the automatic tab. Tap the button below to open Stripe securely, or copy the link.
        </p>
        <a
          href={url}
          target="_top"
          rel="noopener noreferrer"
          className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm"
          data-testid={`${testIdPrefix}-open`}
          onClick={onClose}
        >
          Open Stripe Billing <ExternalLink className="w-4 h-4"/>
        </a>
        <button
          type="button"
          onClick={handleCopy}
          className="mt-2 w-full text-xs text-fg-muted hover:text-brand inline-flex items-center justify-center gap-1.5"
          data-testid={`${testIdPrefix}-copy`}
        >
          <Copy className="w-3.5 h-3.5"/> Copy link
        </button>
        <div className="mt-3 text-[10px] font-mono text-fg-muted break-all bg-bg-secondary/40 p-2 rounded border border-soft">
          {url}
        </div>
      </div>
    </div>
  );
}
